import { toast } from 'sonner';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';
import { AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import api from '../api';

function useContainerWidth(ref) {
  const [width, setWidth] = useState(300);
  useEffect(() => {
    const update = () => { if (ref.current) setWidth(ref.current.offsetWidth - 32); };
    update();
    window.addEventListener('resize', update);
    const timer = setTimeout(update, 200);
    return () => { window.removeEventListener('resize', update); clearTimeout(timer); };
  }, [ref]);
  return width;
}

function LenderDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [simulatorAmount, setSimulatorAmount] = useState(0);  // Will be set from rate tiers
  const [showLoanRequestModal, setShowLoanRequestModal] = useState(false);
  const [requestAmount, setRequestAmount] = useState('');
  const [requestNotes, setRequestNotes] = useState('');
  const [suggestedRate, setSuggestedRate] = useState(0);
  const [rateTiers, setRateTiers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [referralBonus, setReferralBonus] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const chartRef = useRef(null);
  const chartWidth = useContainerWidth(chartRef);

  useEffect(() => {
    checkProfileAndFetchData();
    fetchRateTiers();
    fetchReferralBonus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkProfileAndFetchData = async () => {
    try {
      // Check if profile is complete
      const profileResponse = await api.get('/api/profile');
      const profile = profileResponse.data.profile;
      
      // If profile is incomplete, redirect to complete profile
      if (!profile.doc_number || !profile.address_full || !profile.iban) {
        navigate('/complete-profile');
        return;
      }
      
      // Profile is complete, fetch dashboard data
      const [metricsRes, loansRes] = await Promise.all([
        api.get('/api/dashboard/metrics'),
        api.get('/api/loans')
      ]);
      setMetrics(metricsRes.data);
      setLoans(loansRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchRateTiers = async () => {
    try {
      const response = await api.get('/api/rate-tiers');
      const tiers = response.data.tiers || [];
      setRateTiers(tiers);
      
      // Set initial simulator amount to minimum from first tier (dynamic!)
      if (tiers.length > 0) {
        const minAmount = Math.min(...tiers.map(t => t.min_capital));
        setSimulatorAmount(minAmount);
      } else {
        setSimulatorAmount(40000); // Fallback
      }
    } catch (error) {
      console.error('Error fetching rate tiers:', error);
      setSimulatorAmount(40000); // Fallback
    }
  };

  const fetchReferralBonus = async () => {
    try {
      const response = await api.get('/api/referral-bonus');
      setReferralBonus(response.data);
    } catch (error) {
      console.error('Error fetching referral bonus:', error);
      // Not critical, bonus is optional
    }
  };

  
  const calculateSuggestedRate = (amount) => {
    if (!amount) {
      setSuggestedRate(0);
      return;
    }
    
    // Get minimum from tiers dynamically
    const minAmount = rateTiers.length > 0 ? Math.min(...rateTiers.map(t => t.min_capital)) : 40000;
    
    if (amount < minAmount) {
      setSuggestedRate(0);
      return;
    }
    
    let rate = 6.0;
    // Iterate in reverse order to match highest tier first
    for (let i = rateTiers.length - 1; i >= 0; i--) {
      const tier = rateTiers[i];
      if (amount >= tier.min_capital) {
        rate = tier.interest_rate;
        break;
      }
    }
    setSuggestedRate(rate);
  };
  
  const handleAmountChange = (value) => {
    setRequestAmount(value);
    calculateSuggestedRate(parseFloat(value));
  };
  
  const handleSubmitLoanRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await api.post('/api/loan-request', {
        requested_amount: parseFloat(requestAmount),
        notes: requestNotes
      });
      
      toast.success('' + (t('investment.success') || 'Solicitud enviada correctamente. Nos pondremos en contacto en breve.'));
      setShowLoanRequestModal(false);
      setRequestAmount('');
      setRequestNotes('');
      setSuggestedRate(0);
    } catch (error) {
      toast.error(t('common.error') + ': ' + (error.response?.data?.detail || t('errors.unknown')));
    } finally {
      setSubmitting(false);
    }
  };

  const generateChartData = () => {
    const data = [];
    const dailyInterest = metrics?.interest_today || 0;
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      data.push({
        date: date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }),
        interest: dailyInterest * (30 - i)
      });
    }
    return data;
  };

  const calculateSimulation = () => {
    // Simulate investing additional amount with rate tiers
    const additionalAmount = simulatorAmount || 40000;
    
    // Calculate rate based on tiers
    let rate = 6.0; // Default rate
    
    if (rateTiers && rateTiers.length > 0) {
      for (let i = rateTiers.length - 1; i >= 0; i--) {
        const tier = rateTiers[i];
        if (tier && tier.min_capital !== undefined && additionalAmount >= tier.min_capital) {
          rate = tier.interest_rate || 6.0;
          break;
        }
      }
    }
    
    // Calculate annual interest for 12 months
    const annualInterest = (additionalAmount * rate) / 100;
    const monthlyInterest = annualInterest / 12;
    const totalReturn = additionalAmount + annualInterest;
    
    return { 
      rate: rate,
      annualInterest: annualInterest,
      monthlyInterest: monthlyInterest,
      totalReturn: totalReturn,
      profit: annualInterest
    };
  };

  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <div className="content-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="content-area">
        <TopBar title={t('sidebar.dashboard')} />

        <main style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto' }} className="fade-in">
          
          {/* Alert: Pending Signature Contracts */}
          {loans.some(l => l.status === 'pending_signature' && l.contract_id) && (
            <div 
              data-testid="pending-signature-alert"
              onClick={() => navigate('/contracts')}
              style={{
                background: 'linear-gradient(135deg, rgba(251,146,60,0.15) 0%, rgba(245,158,11,0.08) 100%)',
                border: '2px solid rgba(251,146,60,0.5)',
                borderRadius: '16px',
                padding: '1.25rem 1.5rem',
                marginBottom: '1.5rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                transition: 'all 0.3s ease',
                animation: 'pulse 2s infinite'
              }}
            >
              <span style={{ fontSize: '2rem' }}>&#9998;</span>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: '700', color: '#fb923c', margin: 0, fontSize: '1rem' }}>
                  {t('contracts.pendingSignature') || 'Tens un contracte pendent de firma!'}
                </p>
                <p style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.6)', margin: '0.25rem 0 0 0' }}>
                  {t('contracts.clickToSign') || 'Fes clic aqui per anar a firmar el teu contracte'}
                </p>
              </div>
              <span style={{ fontSize: '1.5rem', color: '#fb923c' }}>&rarr;</span>
            </div>
          )}

          {/* Compact KPI Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {[
              { label: t('dashboard.totalCapital'), value: `€${metrics?.total_capital_invested?.toLocaleString('es-ES') || '0'}`, color: '#60a5fa', icon: '💼' },
              { label: t('dashboard.interestToday'), value: `€${metrics?.interest_today?.toFixed(2) || '0.00'}`, color: '#34d399', icon: '💸' },
              { label: t('dashboard.interestMonth'), value: `€${metrics?.interest_this_month?.toFixed(2) || '0.00'}`, color: '#fb923c', icon: '📅' },
              { label: i18n.language === 'ca' ? 'Interessos Anuals' : 'Intereses Anuales', value: `€${metrics?.interest_annual?.toFixed(2) || '0.00'}`, color: '#c084fc', icon: '📊' },
              { label: i18n.language === 'ca' ? 'Acumulat Total' : 'Acumulado Total', value: `€${metrics?.interest_accumulated?.toFixed(2) || '0.00'}`, color: '#f472b6', icon: '📈' },
              { label: t('dashboard.estimatedReturn'), value: `€${metrics?.estimated_total_return?.toLocaleString('es-ES', {minimumFractionDigits: 2}) || '0'}`, color: '#fbbf24', icon: '🎯' }
            ].map(({ label, value, color, icon }) => (
              <div key={label} style={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #141414 100%)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px',
                padding: '1rem',
                transition: 'all 0.3s ease'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', fontWeight: '500' }}>{label}</span>
                  <span style={{ fontSize: '1rem' }}>{icon}</span>
                </div>
                <p style={{ fontSize: '1.5rem', fontWeight: '700', color: color, lineHeight: '1' }}>
                  {value}
                </p>
              </div>
            ))}
          </div>


          {/* Referral Bonus Section - Only show if has referrals */}
          {referralBonus && referralBonus.referred_count > 0 && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(5,150,105,0.05) 100%)',
              border: '2px solid rgba(16,185,129,0.3)',
              borderRadius: '16px',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <span style={{ fontSize: '2rem' }}>🎁</span>
                <div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#10b981', margin: 0 }}>
                    {t('referral.title')}
                  </h3>
                  <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', margin: 0 }}>
                    {t('referral.subtitle')}
                  </p>
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ 
                  background: 'rgba(16,185,129,0.1)', 
                  padding: '1rem', 
                  borderRadius: '12px',
                  border: '1px solid rgba(16,185,129,0.2)'
                }}>
                  <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
                    {t('referral.activeReferrals')}
                  </p>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10b981' }}>
                    {referralBonus.referred_count}
                  </p>
                </div>

                <div style={{ 
                  background: 'rgba(16,185,129,0.1)', 
                  padding: '1rem', 
                  borderRadius: '12px',
                  border: '1px solid rgba(16,185,129,0.2)'
                }}>
                  <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
                    {t('referral.totalReferredCapital')}
                  </p>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10b981' }}>
                    €{referralBonus.total_referred_capital?.toLocaleString('es-ES') || '0'}
                  </p>
                </div>

                <div style={{ 
                  background: 'rgba(16,185,129,0.1)', 
                  padding: '1rem', 
                  borderRadius: '12px',
                  border: '1px solid rgba(16,185,129,0.2)'
                }}>
                  <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
                    {t('referral.annualBonusGross')}
                  </p>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10b981' }}>
                    €{referralBonus.bonus_accumulated?.toLocaleString('es-ES', {minimumFractionDigits: 2}) || '0.00'}
                  </p>
                </div>

                <div style={{ 
                  background: 'linear-gradient(135deg, rgba(251,191,36,0.15) 0%, rgba(245,158,11,0.1) 100%)', 
                  padding: '1rem', 
                  borderRadius: '12px',
                  border: '2px solid rgba(251,191,36,0.4)'
                }}>
                  <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
                    {i18n.language === 'ca' ? 'Bonus Acumulat Avui' : 'Bonus Acumulado Hoy'}
                  </p>
                  <p style={{ fontSize: '1.75rem', fontWeight: '700', color: '#fbbf24' }}>
                    €{referralBonus.bonus_accumulated?.toLocaleString('es-ES', {minimumFractionDigits: 2}) || '0.00'}
                  </p>
                  <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.25rem' }}>
                    Net: €{referralBonus.bonus_accumulated_net?.toLocaleString('es-ES', {minimumFractionDigits: 2}) || '0.00'}
                  </p>
                </div>

                <div style={{ 
                  background: 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(5,150,105,0.15) 100%)', 
                  padding: '1rem', 
                  borderRadius: '12px',
                  border: '2px solid rgba(16,185,129,0.4)'
                }}>
                  <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
                    {t('referral.bonusNetAfterTax')}
                  </p>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10b981' }}>
                    €{referralBonus.annual_bonus_net?.toLocaleString('es-ES', {minimumFractionDigits: 2}) || '0.00'}
                  </p>
                  <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.25rem' }}>
                    {i18n.language === 'ca' ? 'Projecció anual' : 'Proyección anual'}
                  </p>
                </div>
              </div>

              {/* TOTAL A REBRE - Desglose completo */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(37,99,235,0.15) 0%, rgba(59,130,246,0.1) 100%)',
                border: '1.5px solid rgba(59,130,246,0.3)',
                borderRadius: '12px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <h4 style={{ fontSize: '0.9rem', fontWeight: '700', color: '#60a5fa', marginBottom: '0.75rem' }}>
                  {t('referral.totalToReceive')}
                </h4>
                
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {/* Intereses propios */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                    <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)' }}>
                      {t('referral.ownInterests')}
                    </span>
                    <span style={{ fontSize: '0.95rem', fontWeight: '700', color: '#60a5fa' }}>
                      €{metrics?.estimated_total_return ? (metrics.estimated_total_return - metrics.total_capital_invested).toLocaleString('es-ES', {minimumFractionDigits: 2}) : '0.00'}
                    </span>
                  </div>
                  
                  {/* Bonus referidos */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: 'rgba(16,185,129,0.05)', borderRadius: '8px' }}>
                    <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)' }}>
                      {t('referral.bonusReferrals')}
                    </span>
                    <span style={{ fontSize: '0.95rem', fontWeight: '700', color: '#10b981' }}>
                      €{referralBonus.bonus_accumulated?.toLocaleString('es-ES', {minimumFractionDigits: 2}) || '0.00'}
                    </span>
                  </div>
                  
                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.15)' }}></div>
                  
                  {/* Subtotal */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)' }}>
                      {t('referral.subtotalGross')}
                    </span>
                    <span style={{ fontSize: '1rem', fontWeight: '700', color: 'white' }}>
                      €{((metrics?.estimated_total_return || 0) - (metrics?.total_capital_invested || 0) + (referralBonus.bonus_accumulated || 0)).toLocaleString('es-ES', {minimumFractionDigits: 2})}
                    </span>
                  </div>
                  
                  {/* Retención */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: 'rgba(239,68,68,0.1)', borderRadius: '8px' }}>
                    <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)' }}>
                      {t('referral.irpfRetention')}
                    </span>
                    <span style={{ fontSize: '0.95rem', fontWeight: '700', color: '#ef4444' }}>
                      -€{(((metrics?.estimated_total_return || 0) - (metrics?.total_capital_invested || 0) + (referralBonus.bonus_accumulated || 0)) * 0.19).toLocaleString('es-ES', {minimumFractionDigits: 2})}
                    </span>
                  </div>
                  
                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.2)' }}></div>
                  
                  {/* TOTAL NETO */}
                  <div style={{ 
                    padding: '0.75rem',
                    background: 'linear-gradient(135deg, rgba(59,130,246,0.2) 0%, rgba(37,99,235,0.15) 100%)',
                    borderRadius: '10px',
                    border: '1.5px solid rgba(59,130,246,0.4)',
                    textAlign: 'center'
                  }}>
                    <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', margin: '0 0 0.25rem 0' }}>
                      {t('referral.totalNetToReceive')}
                    </p>
                    <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#60a5fa', margin: 0 }}>
                      €{(((metrics?.estimated_total_return || 0) - (metrics?.total_capital_invested || 0) + (referralBonus.bonus_accumulated || 0)) * 0.81).toLocaleString('es-ES', {minimumFractionDigits: 2})}
                    </p>
                  </div>
                </div>
              </div>

              {/* Desglose de referidos */}
              {referralBonus.referred_lenders && referralBonus.referred_lenders.length > 0 && (
                <details style={{ marginTop: '1rem' }}>
                  <summary style={{ 
                    cursor: 'pointer', 
                    fontSize: '0.875rem', 
                    color: 'rgba(255,255,255,0.7)',
                    fontWeight: '600',
                    padding: '0.5rem',
                    borderRadius: '8px',
                    background: 'rgba(16,185,129,0.05)'
                  }}>
                    📋 {t('referral.viewReferralDetail')} ({referralBonus.referred_lenders.length})
                  </summary>
                  <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.5rem' }}>
                    {referralBonus.referred_lenders.map((ref, idx) => (
                      <div key={idx} style={{
                        background: 'rgba(255,255,255,0.03)',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <p style={{ fontSize: '0.875rem', fontWeight: '600', color: 'white' }}>
                            {ref.name}
                          </p>
                          <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                            {ref.email}
                            {ref.active_loans === 0 && ref.total_loans > 0 && (
                              <span style={{ marginLeft: '0.5rem', color: '#ef4444', fontSize: '0.65rem' }}>
                                ({i18n.language === 'ca' ? 'Cancel·lat' : 'Cancelado'})
                              </span>
                            )}
                          </p>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 0 }}>
                          <p style={{ fontSize: '0.8rem', fontWeight: '700', color: ref.active_loans > 0 ? '#10b981' : '#9ca3af' }}>
                            €{ref.active_capital?.toLocaleString('es-ES') || '0'}
                          </p>
                          <p style={{ fontSize: '0.65rem', color: '#fbbf24', whiteSpace: 'nowrap' }}>
                            €{ref.bonus_accumulated?.toFixed(2) || '0.00'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                background: 'rgba(255,255,255,0.03)',
                borderRadius: '12px',
                fontSize: '0.8rem',
                color: 'rgba(255,255,255,0.6)',
                lineHeight: '1.5'
              }}>
                ℹ️ <strong>{t('referral.fiscalNote')}</strong>
              </div>
            </div>
          )}


          {/* Compact Chart */}
          <div ref={chartRef} style={{
            background: 'linear-gradient(135deg, #1a1a1a 0%, #141414 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '16px',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)', marginBottom: '0.75rem' }}>
              📈 {t('dashboard.interestEvolution')}
            </h3>
            <AreaChart width={chartWidth} height={200} data={generateChartData()}>
              <defs>
                <linearGradient id="colorGreen" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#34d399" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#34d399" stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="date" stroke="rgba(255,255,255,0.3)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} />
              <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} width={50} />
              <Tooltip 
                contentStyle={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
                itemStyle={{ color: '#34d399' }}
              />
              <Area type="monotone" dataKey="interest" stroke="#34d399" strokeWidth={2} fill="url(#colorGreen)" />
            </AreaChart>
          </div>

          {/* Compact Simulator */}
          <div style={{
            background: 'linear-gradient(135deg, #1a1a1a 0%, #141414 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '16px',
            padding: '1.25rem',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)', marginBottom: '1rem' }}>
              📊 {t('simulator.title') || 'Simulador de Préstec'}
            </h3>
            <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1rem' }}>
              {t('simulator.simulate') || 'Simula quant guanyaries invertint:'}
            </p>
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)' }}>{t('simulator.amount') || 'Import'}:</span>
                <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#FF9800' }}>
                  {simulatorAmount.toLocaleString('es-ES')}€
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)' }}>{t('simulator.interestRate') || "Tipus d'interès"}:</span>
                <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#34d399' }}>
                  {calculateSimulation().rate}%
                </span>
              </div>
            </div>
            
            {/* Slider with tier labels */}
            <div style={{ marginBottom: '1.25rem' }}>
              <input
                type="range"
                min={20000}
                max={180000}
                step="5000"
                value={simulatorAmount || 40000}
                onChange={(e) => setSimulatorAmount(parseInt(e.target.value))}
                className="modern-slider"
                data-testid="simulator-slider"
                style={{ width: '100%', marginBottom: '0.5rem' }}
              />
              {/* Tier labels row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', padding: '0 2px' }}>
                {rateTiers.map((tier, i) => (
                  <span key={i} style={{ color: 'rgba(255,255,255,0.4)', cursor: 'pointer', textAlign: 'center' }}
                    onClick={() => setSimulatorAmount(tier.min_capital)}>
                    <span style={{ display: 'block' }}>{(tier.min_capital/1000).toFixed(0)}k</span>
                    <span style={{ color: '#ff9900', fontWeight: '600' }}>{tier.interest_rate}%</span>
                  </span>
                ))}
                <span style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
                  <span style={{ display: 'block' }}>180k</span>
                </span>
              </div>
            </div>
            
            {/* Earnings breakdown - Daily and Monthly highlighted */}
            <div style={{ display: 'grid', gap: '1rem', marginBottom: '1rem' }}>
              {/* Daily earnings - HIGHLIGHTED GREEN */}
              <div style={{ 
                padding: '1rem', 
                background: 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(16,185,129,0.1) 100%)', 
                borderRadius: '12px', 
                border: '2px solid rgba(16,185,129,0.4)'
              }}>
                <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', marginBottom: '0.25rem', fontWeight: '600' }}>
                  💰 {t('simulator.earningsDay') || 'GUANYS PER DIA'}
                </p>
                <p style={{ fontSize: '1.75rem', fontWeight: '700', color: '#34d399', lineHeight: '1' }}>
                  +{(calculateSimulation().annualInterest / 365).toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})}€
                </p>
                <p style={{ fontSize: '0.75rem', color: '#34d399', marginTop: '0.25rem', opacity: 0.8 }}>
                  {t('simulator.dailyGrowth') || 'El teu diner creix cada dia!'}
                </p>
              </div>
              
              {/* Monthly earnings - HIGHLIGHTED BLUE */}
              <div style={{ 
                padding: '1rem', 
                background: 'linear-gradient(135deg, rgba(59,130,246,0.2) 0%, rgba(59,130,246,0.1) 100%)', 
                borderRadius: '12px', 
                border: '2px solid rgba(59,130,246,0.4)'
              }}>
                <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', marginBottom: '0.25rem', fontWeight: '600' }}>
                  📅 {t('simulator.earningsMonth') || 'GUANYS PER MES'}
                </p>
                <p style={{ fontSize: '1.75rem', fontWeight: '700', color: '#60a5fa', lineHeight: '1' }}>
                  +{calculateSimulation().monthlyInterest.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})}€
                </p>
                <p style={{ fontSize: '0.75rem', color: '#60a5fa', marginTop: '0.25rem', opacity: 0.8 }}>
                  {t('simulator.monthlyIncome') || 'Ingressos mensuals garantits'}
                </p>
              </div>
              
              {/* Annual earnings - HIGHLIGHTED PURPLE (same size) */}
              <div style={{ 
                padding: '1rem', 
                background: 'linear-gradient(135deg, rgba(168,85,247,0.2) 0%, rgba(168,85,247,0.1) 100%)', 
                borderRadius: '12px', 
                border: '2px solid rgba(168,85,247,0.4)'
              }}>
                <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', marginBottom: '0.25rem', fontWeight: '600' }}>
                  📈 {t('simulator.earningsYear') || 'GUANYS PER ANY'}
                </p>
                <p style={{ fontSize: '1.75rem', fontWeight: '700', color: '#a855f7', lineHeight: '1' }}>
                  +{calculateSimulation().annualInterest.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})}€
                </p>
                <p style={{ fontSize: '0.75rem', color: '#a855f7', marginTop: '0.25rem', opacity: 0.8 }}>
                  {t('simulator.totalYield') || 'Rendiment total anual'}
                </p>
              </div>
            </div>
            
            <div style={{ padding: '0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', textAlign: 'center' }}>
              <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
                {t('simulator.totalReturn') || 'Retorn total a 12 mesos'}: <strong style={{ color: '#34d399', fontSize: '1rem' }}>{calculateSimulation().totalReturn.toLocaleString('es-ES', {minimumFractionDigits: 2})}€</strong>
              </p>
            </div>
          </div>

          {/* Main CTA Button - Request New Loan (attractive, normal size) */}
          <button
            onClick={() => setShowLoanRequestModal(true)}
            style={{
              width: '100%',
              marginBottom: '1.5rem',
              padding: '1rem',
              fontSize: '1rem',
              fontWeight: '600',
              background: 'linear-gradient(135deg, #FF9800 0%, #fb8c00 100%)',
              border: 'none',
              color: 'white',
              boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(255, 152, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 152, 0, 0.3)';
            }}
            data-testid="request-new-loan-button"
          >
            <span style={{ fontSize: '1.1rem' }}>💰</span>
            <span>{t('loanRequests.requestNew')}</span>
          </button>

          {/* Loan Request Modal with Rate Tiers Simulator */}
          {showLoanRequestModal && (
            <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowLoanRequestModal(false); }}>
              <div className="modal-content" style={{ maxWidth: '600px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'white', margin: 0 }}>
                    💰 Sol·licitar Nou Préstec
                  </h3>
                  <button
                    onClick={() => setShowLoanRequestModal(false)}
                    style={{
                      background: '#f3f4f6',
                      border: 'none',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      color: '#333'
                    }}
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleSubmitLoanRequest} style={{ display: 'grid', gap: '1.5rem' }}>
                  {/* Amount Input */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)' }}>
                      Import Sol·licitat (€)
                    </label>
                    <input
                      type="number"
                      required
                      min={rateTiers.length > 0 ? Math.min(...rateTiers.map(t => t.min_capital)) : 40000}
                      step="1000"
                      value={requestAmount}
                      onChange={(e) => handleAmountChange(e.target.value)}
                      placeholder={(rateTiers.length > 0 ? Math.min(...rateTiers.map(t => t.min_capital)) : 40000).toLocaleString('es-ES')}
                      style={{ 
                        width: '100%',
                        fontSize: '1.5rem',
                        fontWeight: '700',
                        padding: '1rem',
                        textAlign: 'center',
                        borderColor: requestAmount && parseFloat(requestAmount) < (rateTiers.length > 0 ? Math.min(...rateTiers.map(t => t.min_capital)) : 40000) 
                          ? '#ef4444' 
                          : 'rgba(255,255,255,0.15)'
                      }}
                      data-testid="loan-request-amount-input"
                    />
                    <p style={{ 
                      fontSize: '0.75rem', 
                      color: requestAmount && parseFloat(requestAmount) < (rateTiers.length > 0 ? Math.min(...rateTiers.map(t => t.min_capital)) : 40000)
                        ? '#ef4444'
                        : 'rgba(255,255,255,0.5)', 
                      marginTop: '0.5rem', 
                      textAlign: 'center',
                      fontWeight: requestAmount && parseFloat(requestAmount) < (rateTiers.length > 0 ? Math.min(...rateTiers.map(t => t.min_capital)) : 40000) ? '600' : '400'
                    }}>
                      {requestAmount && parseFloat(requestAmount) < (rateTiers.length > 0 ? Math.min(...rateTiers.map(t => t.min_capital)) : 40000)
                        ? `⚠️ L'import mínim és ${(rateTiers.length > 0 ? Math.min(...rateTiers.map(t => t.min_capital)) : 40000).toLocaleString('es-ES')}€`
                        : `Mínim: ${(rateTiers.length > 0 ? Math.min(...rateTiers.map(t => t.min_capital)) : 40000).toLocaleString('es-ES')}€`
                      }
                    </p>
                  </div>

                  {/* Rate Simulator */}
                  {suggestedRate > 0 && (
                    <div style={{ 
                      padding: '1.5rem', 
                      background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(52,211,153,0.1) 100%)', 
                      borderRadius: '12px',
                      border: '1px solid rgba(16,185,129,0.3)'
                    }}>
                      <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', marginBottom: '0.5rem' }}>
                        📊 {t('loanRequest.suggestedRate') || "Tipus d'interès"}
                      </p>
                      <p style={{ fontSize: '3rem', fontWeight: '700', color: '#34d399', lineHeight: '1' }}>
                        {suggestedRate}%
                      </p>
                      <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)', marginTop: '0.5rem' }}>
                        Segons els barems actuals per a {parseFloat(requestAmount).toLocaleString('es-ES')}€
                      </p>
                      
                      {/* Tiers info */}
                      <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem' }}>Barems actuals:</p>
                        {rateTiers && rateTiers.length > 0 ? rateTiers.map((tier, i) => (
                          <div key={i} style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>
                            • {(tier.min_capital || 0).toLocaleString()}€ - {tier.max_capital ? tier.max_capital.toLocaleString() + '€' : '∞'} → {tier.interest_rate || 0}%
                          </div>
                        )) : (
                          <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                            No hi ha barems configurats
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)' }}>
                      Notes (opcional)
                    </label>
                    <textarea
                      value={requestNotes}
                      onChange={(e) => setRequestNotes(e.target.value)}
                      rows={3}
                      placeholder="Preferències de termini, condicions especials, etc."
                      style={{ width: '100%', fontSize: '0.95rem' }}
                    />
                  </div>

                  <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)', padding: '0.75rem', background: 'rgba(59,130,246,0.1)', borderRadius: '8px', border: '1px solid rgba(59,130,246,0.2)' }}>
                    ℹ️ En enviar, rebrem la teva sol·licitud i ens posarem en contacte amb tu en breu per confirmar els detalls.
                  </p>

                  {/* Buttons */}
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      type="button"
                      onClick={() => setShowLoanRequestModal(false)}
                      style={{
                        flex: 1,
                        padding: '0.875rem',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: '10px',
                        color: 'rgba(255,255,255,0.7)',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || !requestAmount || parseFloat(requestAmount) < (rateTiers.length > 0 ? Math.min(...rateTiers.map(t => t.min_capital)) : 40000)}
                      style={{
                        flex: 1,
                        padding: '0.875rem',
                        background: submitting || !requestAmount || parseFloat(requestAmount) < (rateTiers.length > 0 ? Math.min(...rateTiers.map(t => t.min_capital)) : 40000)
                          ? 'rgba(255,255,255,0.1)'
                          : 'linear-gradient(135deg, #007a33 0%, #00a843 100%)',
                        color: submitting || !requestAmount || parseFloat(requestAmount) < (rateTiers.length > 0 ? Math.min(...rateTiers.map(t => t.min_capital)) : 40000)
                          ? 'rgba(255,255,255,0.5)'
                          : 'white',
                        border: 'none',
                        borderRadius: '10px',
                        fontWeight: '600',
                        cursor: submitting || !requestAmount || parseFloat(requestAmount) < (rateTiers.length > 0 ? Math.min(...rateTiers.map(t => t.min_capital)) : 40000)
                          ? 'not-allowed' 
                          : 'pointer'
                      }}
                      data-testid="submit-loan-request-button"
                    >
                      {submitting ? '⏳ Enviant...' : '✓ Enviar Sol·licitud'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Compact Loans List */}
          {loans.filter(l => l.status === 'active').length > 0 && (
            <div style={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #141414 100%)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '16px',
              padding: '1.25rem'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)', marginBottom: '1rem' }}>
                💼 {t('dashboard.activeLoans')}
              </h3>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {loans.filter(l => l.status === 'active').slice(0, 5).map((loan) => (
                  <div
                    key={loan._id}
                    onClick={() => navigate(`/loans/${loan._id}`)}
                    style={{
                      padding: '1rem',
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      border: '1px solid transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(168,85,247,0.08)';
                      e.currentTarget.style.borderColor = 'rgba(168,85,247,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                      e.currentTarget.style.borderColor = 'transparent';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <p style={{ fontSize: '1.125rem', fontWeight: '700', color: 'white' }}>
                          €{loan.capital.toLocaleString('es-ES')}
                        </p>
                        <p style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.25rem' }}>
                          {loan.interest_rate}% • {loan.days_remaining || 0} {t('dashboard.days')}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: '1rem', fontWeight: '700', color: '#34d399' }}>
                          €{loan.interest_generated?.toFixed(2) || '0.00'}
                        </p>
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>{t('loanDetail.interestGenerated').toLowerCase()}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default LenderDashboard;
